/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, TextField } from '@components/atoms'
import { FullModalLayout } from '@components/molecules'
import {
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	SelectChangeEvent,
} from '@mui/material'
import {
	useAppSelector,
	useAppDispatch,
	shipmentActions,
	boxActions,
	routeActions,
	ApiGetDataStatus,
} from '@redux/index'
import {
	BoxType,
	DispatchDetailType,
	ShipmentCommodityType,
	ShipmentServiceType,
} from '@schemas'
import {
	mono,
	boFont,
	boShared,
	boMedia,
} from '@components/systems/backoffice/backoffice.tokens'
import { detailPath } from './DispatchDetail'

const SERVICE_OPTIONS: { value: ShipmentServiceType; label: string }[] = [
	{ value: 'INTERNATIONAL_ECONOMY', label: 'International Economy' },
	{ value: 'INTERNATIONAL_PRIORITY', label: 'International Priority' },
	{ value: 'FEDEX_INTERNATIONAL_CONNECT_PLUS', label: 'International Connect Plus' },
]

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * 라벨 만들기 (`/main/shipping/:dispatchId/label/new`) — 전체 화면 한 장.
 *
 * 상세와 **따로 둔다**: 이것은 읽는 화면이 아니라 누르면 FedEx 에 등록되고 요금이 나가는
 * 쓰기 화면이다(SHIPPING_PLAN §2.2.1). 초기값은 상세 응답의 `prefill` 을 그대로 쓴다 —
 * 수취인·주소는 배송 건 스냅샷(주문 건이면 주문 값)이라 여기서 고치지 않는다.
 *
 * 성공하면 사가가 인쇄 창을 열고 라벨 보기로 옮긴다.
 */
export const ShipmentLabelCreate = () => {
	const reduxDispatch = useAppDispatch()

	const dispatchId: string = useAppSelector(state => state.shipment.detailDispatchId)
	const detail: DispatchDetailType | null = useAppSelector(state => state.shipment.detail)
	const detailStatus: ApiGetDataStatus = useAppSelector(
		state => state.shipment.detailStatus,
	)
	const creating: boolean = useAppSelector(state => state.shipment.creating)
	const message: string = useAppSelector(state => state.shipment.message)
	const boxList: BoxType[] = useAppSelector(state => state.box.boxList)

	const prefill = detail?.prefill

	// form
	const [boxId, setBoxId] = React.useState<string>('')
	const [widthCm, setWidthCm] = React.useState<string>('')
	const [heightCm, setHeightCm] = React.useState<string>('')
	const [depthCm, setDepthCm] = React.useState<string>('')
	const [weightKg, setWeightKg] = React.useState<string>('')
	const [serviceType, setServiceType] = React.useState<ShipmentServiceType>(
		'INTERNATIONAL_ECONOMY',
	)
	const [commodities, setCommodities] = React.useState<ShipmentCommodityType[]>([])

	// 이 화면이 서 있는 동안은 한 줄 알림을 토스트로 넘기지 않는다 — 오류가 폼 옆에 남아야 읽힌다
	React.useEffect(() => {
		reduxDispatch(shipmentActions.LABEL_FORM_OPEN(true))
		reduxDispatch(boxActions.TRIGGER_INIT_BOX_PAGE(true))
		return () => {
			reduxDispatch(shipmentActions.LABEL_FORM_OPEN(false))
		}
	}, [reduxDispatch])

	React.useEffect(() => {
		if (prefill === undefined) return
		setBoxId(prefill.boxId)
		setWidthCm(String(prefill.widthCm))
		setHeightCm(String(prefill.heightCm))
		setDepthCm(String(prefill.depthCm))
		setWeightKg(String(prefill.weightKg))
		setServiceType(prefill.serviceType)
		setCommodities(prefill.commodities)
	}, [prefill])

	const netKg: number = React.useMemo<number>(
		() =>
			prefill === undefined
				? 0
				: prefill.lines.reduce(
						(acc: number, l) => acc + Number(l.netKg ?? 0) * Number(l.quantity),
						0,
				  ),
		[prefill],
	)

	const onClose = React.useCallback(() => {
		if (creating) return
		reduxDispatch(routeActions.NAVIGATE_PATH(detailPath(dispatchId)))
	}, [creating, dispatchId, reduxDispatch])

	const onBoxChange = (e: SelectChangeEvent) => {
		const nextId = e.target.value
		setBoxId(nextId)
		const box = boxList.find(b => b.id === nextId)
		if (box !== undefined) {
			setWidthCm(String(box.widthCm))
			setHeightCm(String(box.heightCm))
			setDepthCm(String(box.depthCm))
			const w = round2(Number(box.tareKg) + netKg)
			setWeightKg(String(w > 0 ? w : 1))
		}
	}

	const onCommodityChange = (
		idx: number,
		key: keyof ShipmentCommodityType,
		value: string,
	) => {
		setCommodities(prev =>
			prev.map((c, i) =>
				i === idx
					? {
							...c,
							[key]: key === 'quantity' || key === 'unitPriceUsd' ? Number(value) : value,
					  }
					: c,
			),
		)
	}

	const numbers = {
		widthCm: Number(widthCm),
		heightCm: Number(heightCm),
		depthCm: Number(depthCm),
		weightKg: Number(weightKg),
	}
	const isValid =
		prefill !== undefined &&
		Object.values(numbers).every(n => Number.isFinite(n) && n > 0) &&
		commodities.length > 0 &&
		commodities.every(
			c => c.description.trim() !== '' && c.quantity > 0 && c.unitPriceUsd >= 0,
		)

	const onSubmit = () => {
		if (!isValid || prefill === undefined) return
		reduxDispatch(
			shipmentActions.TRIGGER_CREATE_FEDEX({
				dispatchId: prefill.dispatchId,
				boxId,
				...numbers,
				serviceType,
				commodities: commodities.map(c => ({
					...c,
					description: c.description.trim(),
					countryOfManufacture: c.countryOfManufacture || 'KR',
				})),
			}),
		)
	}

	if (dispatchId === '') return null

	const r = prefill?.recipient
	const title: string =
		detail?.dispatch.orderId !== undefined && detail.dispatch.orderId !== ''
			? detail.dispatch.orderId
			: detail?.dispatch.partyName ?? dispatchId

	return (
		<FullModalLayout
			overlay
			size="large"
			title={title}
			onCloseClick={onClose}
			customTitle={
				<View className="labelHead" css={headStyle}>
					<span className="boKicker">라벨 생성</span>
					<h1 className="labelNumber">{title}</h1>
				</View>
			}
		>
			<View css={style}>
				{prefill === undefined || r === undefined ? (
					<View className="boSection">
						<Text className="boEmpty">
							{detailStatus === 'loading' || detailStatus === 'init'
								? '배송 건을 읽는 중…'
								: '이 배송 건을 찾지 못했습니다.'}
						</Text>
					</View>
				) : (
					<>
						{/* 읽기 전용 — 주소가 틀렸으면 주문에서 고친다 */}
						<View className="boSection">
							<Text className="boSectionTitle">수취인</Text>
							<View className="boSpecRow top">
								<span className="boSpecLabel">받는 사람</span>
								<span className="boSpecValue">
									<span className="block">
										{r.name} · {r.phone}
									</span>
									<span className="block">{r.streetLines.join(', ')}</span>
									<span className="block">
										{[r.city, r.stateOrProvinceCode, r.postalCode, r.countryCode]
											.filter(s => s !== '')
											.join(' ')}
									</span>
								</span>
							</View>
							<Text className="boNote">
								주소와 전화는 배송 건에서 읽는다. 틀렸으면 배송 건(수동 건) 또는 주문을
								고치고 다시 온다.
							</Text>
						</View>

						{/* 포장 */}
						<View className="boSection">
							<Text className="boSectionTitle">포장</Text>
							<View className="formRow">
								<FormControl size="small" fullWidth>
									<InputLabel id="label-box-select">박스</InputLabel>
									<Select
										labelId="label-box-select"
										label="박스"
										value={boxId}
										onChange={onBoxChange}
									>
										{boxList
											.filter(b => b.useYn === 1 || b.id === boxId)
											.map(b => (
												<MenuItem key={b.id} value={b.id}>
													{b.code} · {b.widthCm}×{b.heightCm}×{b.depthCm} cm · 빈 박스{' '}
													{b.tareKg} kg
												</MenuItem>
											))}
									</Select>
								</FormControl>
								<FormControl size="small" fullWidth>
									<InputLabel id="label-service-select">서비스</InputLabel>
									<Select
										labelId="label-service-select"
										label="서비스"
										value={serviceType}
										onChange={(e: SelectChangeEvent) =>
											setServiceType(e.target.value as ShipmentServiceType)
										}
									>
										{SERVICE_OPTIONS.map(o => (
											<MenuItem key={o.value} value={o.value}>
												{o.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</View>

							<View className="formRow four">
								<TextField
									label="가로(cm)"
									size="small"
									type="number"
									value={widthCm}
									onChange={e => setWidthCm(e.target.value)}
								/>
								<TextField
									label="세로(cm)"
									size="small"
									type="number"
									value={heightCm}
									onChange={e => setHeightCm(e.target.value)}
								/>
								<TextField
									label="높이(cm)"
									size="small"
									type="number"
									value={depthCm}
									onChange={e => setDepthCm(e.target.value)}
								/>
								<TextField
									label="무게(kg)"
									size="small"
									type="number"
									value={weightKg}
									onChange={e => setWeightKg(e.target.value)}
									helperText={`제품 ${round2(netKg)} kg + 빈 박스`}
								/>
							</View>
							<Text className="boNote">실제로 포장한 값으로 고친다.</Text>
						</View>

						{/* 통관 */}
						<View className="boSection">
							<Text className="boSectionTitle">통관 품목</Text>
							{commodities.map((c, idx) => (
								<View className="formRow commodity" key={idx}>
									<TextField
										label="품목"
										size="small"
										value={c.description}
										onChange={e => onCommodityChange(idx, 'description', e.target.value)}
									/>
									<TextField
										label="수량"
										size="small"
										type="number"
										value={c.quantity}
										onChange={e => onCommodityChange(idx, 'quantity', e.target.value)}
									/>
									<TextField
										label="단가(USD)"
										size="small"
										type="number"
										value={c.unitPriceUsd}
										onChange={e => onCommodityChange(idx, 'unitPriceUsd', e.target.value)}
									/>
								</View>
							))}
							<Text className="boNote">
								관세는 발송인 부담(SENDER)으로 신고된다. 라벨은 4×6 PDF 로 저장된다.
							</Text>
						</View>

						{message !== '' && <Text className="boError">{message}</Text>}

						<View className="actionBar">
							<Text className="chargeNote">
								생성하면 FedEx 에 등록되고 요금이 청구된다. 송장번호가 바로 발급된다.
							</Text>
							<View className="actionRight">
								<button
									type="button"
									className="boBtnGhost"
									onClick={onClose}
									disabled={creating}
								>
									취소
								</button>
								<button
									type="button"
									className="boBtn"
									onClick={onSubmit}
									disabled={!isValid || creating}
								>
									{creating ? '생성 중…' : '라벨 생성'}
								</button>
							</View>
						</View>
					</>
				)}
			</View>
		</FullModalLayout>
	)
}

const headStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	fontFamily: boFont.ui,

	'.boKicker': {
		fontSize: 11,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},
	'.labelNumber': {
		margin: 0,
		fontSize: 'clamp(24px, 2.6vw, 32px)',
		fontWeight: 200,
		lineHeight: 1.1,
		color: mono.ink,
		fontVariantNumeric: 'tabular-nums',
	},
})

const style = css({
	...boShared,
	paddingTop: 8,

	'.boSection': {
		...boShared['.boSection'],
		'.boSectionTitle': { marginBottom: 8 },
	},
	'.boSpecRow': {
		...boShared['.boSpecRow'],
		gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
		borderBottom: 0,
		'&.top': { alignItems: 'flex-start' },
	},
	'.boSpecValue': {
		...boShared['.boSpecValue'],
		'.block': { display: 'block' },
	},
	'.boNote': { fontSize: 12, color: mono.dim, marginTop: 8, lineHeight: 1.6 },

	/*
		폼은 넓은 화면에서도 읽는 폭을 지킨다 — 입력 칸이 1200px 에 늘어지면 눈이 줄을 놓친다.
		화면은 꽉 채우되(FullModalLayout large) 폼 자체는 720 에서 멈춘다.
	*/
	'.boSection, .actionBar': { maxWidth: 720 },

	'.formRow': {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 12,
		marginBottom: 12,
		'&.four': { gridTemplateColumns: 'repeat(4, 1fr)' },
		'&.commodity': { gridTemplateColumns: '2fr 1fr 1fr' },
	},

	'.actionBar': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		paddingTop: 20,
		borderTop: `1px solid ${mono.line}`,
		'.chargeNote': { fontSize: 12, color: mono.dim },
		'.actionRight': { display: 'flex', alignItems: 'center', gap: 8 },
	},
	// 폰 — 여러 열 폼은 한 열로 (2026-09-29)
	[boMedia.phone]: {
		'.formRow, .formRow.two, .formRow.three, .formRow.four, .formRow.commodity': {
			gridTemplateColumns: '1fr',
			maxWidth: '100%',
		},
	},
})
